import NotFound from "./NotFound";
import { useParams, Link } from "react-router-dom";
import { Phone, CalendarCheck, ShieldCheck, Clock, MapPin, Check } from "lucide-react";
import SubLayout from "@/components/site/SubLayout";
import Breadcrumbs from "@/components/site/Breadcrumbs";
import CtaBlock from "@/components/site/CtaBlock";
import Seo from "@/seo/Seo";
import { breadcrumbSchema, serviceSchema, faqSchema, localBusinessSchema } from "@/seo/schemas";
import { SITE } from "@/data/site";
import { SERVICES, serviceBySlug } from "@/data/services";
import { CITIES } from "@/data/cities";

export default function ServicePage() {
  const { slug } = useParams();
  const svc = serviceBySlug(slug);
  if (!svc) return <NotFound />;

  const path = `/services/${svc.slug}`;
  const title = svc.seoTitle || `${svc.name} | Orange County & Los Angeles`;
  const description = svc.meta;
  const others = SERVICES.filter((s) => s.slug !== slug);

  const faqs = [
    { q: `Is ${svc.name.toLowerCase()} available same-day?`, a: `Yes — we offer same-day and next-day appointments across Orange County and Los Angeles County. Call ${SITE.phone} to lock in a time.` },
    { q: `Do you come to my location?`, a: `Yes. We're fully mobile and California DMV-licensed. We meet you at home, work, or wherever your vehicle is stored.` },
    { q: `What do I need to have ready?`, a: `Have the vehicle and your out-of-state title or registration available. We'll confirm anything else specific to your situation when you book.` },
    { q: `How much does it cost?`, a: `Pricing depends on the vehicle and travel distance. Call ${SITE.phone} for a fast, upfront quote; any travel fee is confirmed before you book.` },
  ];

  const schema = [
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/#services" }, { name: svc.name, path }]),
    serviceSchema({ name: svc.name, description, path }),
    faqSchema(faqs),
    localBusinessSchema(),
  ];

  return (
    <SubLayout>
      <Seo title={title} description={description} path={path} schema={schema} />

      <section className="bg-gradient-to-b from-mist to-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10 lg:py-14">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/#services" }, { name: svc.name, path }]} />
          <div className="inline-flex items-center gap-2 rounded-full bg-brand/10 text-brand px-3.5 py-1.5 text-xs font-semibold mt-6"><ShieldCheck size={14} /> California DMV Licensed</div>
          <h1 className="font-display font-extrabold text-ink tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">{svc.h1}</h1>
          <p className="text-slate2 text-lg mt-4 leading-relaxed">{svc.intro}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href={SITE.phoneHref} className="btn-accent rounded-full px-7 py-4 text-base font-semibold flex items-center justify-center gap-2"><Phone size={18} /> Call Now</a>
            <Link to="/book" className="btn-outline rounded-full px-7 py-4 text-base font-semibold flex items-center justify-center gap-2"><CalendarCheck size={18} /> Book Appointment</Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate2">
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-brand" /> Licensed & Bonded</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-brand" /> Same-Day Service</span>
            <span className="flex items-center gap-2"><MapPin size={16} className="text-brand" /> OC & LA County</span>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14 lg:py-20 space-y-14">
        <section className="grid sm:grid-cols-2 gap-4">
          {svc.bullets.map((b) => (
            <div key={b} className="card-soft hover:!translate-y-0 p-6 flex gap-3">
              <Check size={20} className="text-brand shrink-0 mt-0.5" /><p className="text-ink font-medium">{b}</p>
            </div>
          ))}
        </section>

        <section>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">About {svc.name}</h2>
          <div className="text-slate2 text-base leading-relaxed mt-4 space-y-4">
            <p>{svc.intro} As a California DMV-licensed and bonded vehicle verifier, The VIN Verifier CA completes the official REG 31 (Verification of Vehicle) form accurately so the DMV accepts it without delays or rejections.</p>
            <p>We proudly serve all of Orange County and Los Angeles County. Because we're fully mobile, there's no reason to wait in a DMV line — we come to your home, office or storage location, inspect the vehicle in about 15–20 minutes, and hand you a completed, signed form ready to submit. Same-day and next-day appointments are available; just call {SITE.phone}.</p>
          </div>
        </section>

        <section>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">How It Works</h2>
          <ol className="mt-6 space-y-4">
            {[["Request a quote", `Call or text ${SITE.phone}, or use our online quote form.`],
              ["We come to you", "We confirm an arrival window and drive to your location in OC or LA County."],
              ["On-site verification", "We record and confirm the VIN and complete the REG 31 form."],
              ["Submit to the DMV", "Take your signed REG 31 to the DMV with your other paperwork."]].map(([t, d], i) => (
              <li key={t} className="flex gap-4"><span className="h-9 w-9 shrink-0 rounded-full bg-brand text-white font-display font-bold flex items-center justify-center">{i + 1}</span><div><h3 className="font-display font-semibold text-ink">{t}</h3><p className="text-slate2 text-sm mt-1">{d}</p></div></li>
            ))}
          </ol>
        </section>

        <section>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="card-soft hover:!translate-y-0 p-6"><h3 className="font-display font-semibold text-ink">{f.q}</h3><p className="text-slate2 text-sm mt-2 leading-relaxed">{f.a}</p></div>
            ))}
          </div>
        </section>

        <CtaBlock heading={`Ready for your ${svc.name.toLowerCase()}?`} sub={`Licensed, mobile, same-day across Orange County & LA. Call ${SITE.phone}.`} />

        <section>
          <h2 className="font-display font-bold text-ink text-xl">Other Services</h2>
          <div className="flex flex-wrap gap-2 mt-4">
            {others.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="inline-flex items-center rounded-full bg-mist border border-gray-100 text-slate2 text-sm px-3 py-1.5 hover:text-brand hover:border-brand/30 transition-colors">{s.name}</Link>
            ))}
          </div>
          <h2 className="font-display font-bold text-ink text-xl mt-8">Popular Service Areas</h2>
          <div className="flex flex-wrap gap-2 mt-4">
            {CITIES.slice(0, 10).map((c) => (
              <Link key={c.slug} to={`/vin-verification/${c.slug}`} className="inline-flex items-center rounded-full bg-mist border border-gray-100 text-slate2 text-sm px-3 py-1.5 hover:text-brand hover:border-brand/30 transition-colors">{c.name}</Link>
            ))}
          </div>
        </section>
      </div>
    </SubLayout>
  );
}
